import { authApi } from './api/auth';
import { apiFetch, setToken } from './api/client';
import { progressApi } from './api/progress';
import {
  simulaApi,
  type SimulaFinalizeAnswer,
  type SimulaFinalizeResponse,
  type SimulaStartResponse,
} from './api/simula';
import { sessionsApi, type SimulaSummaryPayload } from './api/sessions';

const CAPTURE_QUERY = 'rw2_live_capture';
const NONCE_PATTERN = /^[A-Za-z0-9_-]{24,64}$/u;

export interface Rw2LiveCaptureBridge {
  login(input: { username: string; password: string }): Promise<{ studentId: string }>;
  start(): Promise<SimulaStartResponse>;
  progress(): Promise<{ simulaCount: number }>;
  finalize(input: {
    simulationId: string;
    answers: SimulaFinalizeAnswer[];
    correlationId: string;
  }): Promise<SimulaFinalizeResponse>;
  result(simulationId: string): Promise<SimulaSummaryPayload>;
  logout(): Promise<{ revoked: true }>;
}

declare global {
  interface Window {
    __AUTOSCUOLA_RW2_CAPTURE__?: Rw2LiveCaptureBridge;
  }
}

export function createRw2LiveCaptureBridge(): Rw2LiveCaptureBridge {
  return Object.freeze({
    async login(input) {
      const result = await authApi.loginStudent(input);
      return { studentId: result.studentId };
    },
    async start() {
      return simulaApi.start('cieca');
    },
    async progress() {
      const summary = await progressApi.summary();
      return { simulaCount: summary.simulaCount };
    },
    async finalize(input) {
      return simulaApi.finalize(
        input.simulationId,
        'cieca',
        input.answers,
        input.correlationId,
      );
    },
    async result(simulationId) {
      return sessionsApi.getSimulaSummary(simulationId);
    },
    async logout() {
      await authApi.logout();
      const revoked = await apiFetch('/api/me', { method: 'GET' });
      setToken(null);
      if (revoked.status !== 401) throw new Error('rw2_capture_session_not_revoked');
      return { revoked: true as const };
    },
  });
}

function installRw2LiveCaptureBridge(): void {
  const nonce = new URLSearchParams(window.location.search).get(CAPTURE_QUERY);
  if (
    window.location.protocol !== 'https:'
    || !window.location.hostname.toLowerCase().includes('staging')
    || !nonce
    || !NONCE_PATTERN.test(nonce)
  ) return;

  const bridge = createRw2LiveCaptureBridge();

  Object.defineProperty(window, '__AUTOSCUOLA_RW2_CAPTURE__', {
    configurable: false,
    enumerable: false,
    value: bridge,
    writable: false,
  });
}

installRw2LiveCaptureBridge();
